import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import { useDispatch } from "react-redux";
import { useSigninWithCode } from "../utils/authHelpers";
import { SET_WARNING, CLOSE_WARNING } from "../actions/type";
import {
  default as ACCESS_LEVELS,
  ADMINS,
  STUDENT
} from "../config/accessLevels";

const MyBestUserLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const signinWithCode = useSigninWithCode();
  const dispatch = useDispatch();

  useEffect(() => {
    const search = qs.parse(location.search, { ignoreQueryPrefix: true });

    signinWithCode(search.code)
      .then(({ data }) => {
        const roles = data.signinWithCode.me.roles || [];

        // If user has no roles, show warning and redirect to home
        if (roles.length === 0) {
          dispatch({
            type: SET_WARNING,
            payload: {
              alert:
                "身分認證錯誤!\n請洽詢您的校長索取登入邀請碼,或是聯繫教務及工程師\n\nIdentity error!\nPlease check invitation code with your principal or Melody consultants.",
              confirm: () => {
                dispatch({ type: CLOSE_WARNING, payload: {} });
                navigate("/");
              }
            }
          });
          return;
        }

        // Check localStorage first (persists after logout)
        let previousUrl = localStorage.getItem("tabletReturnPath");
        if (previousUrl) {
          // Clear it after reading
          localStorage.removeItem("tabletReturnPath");
        }

        // Fallback to sessionStorage if no tabletReturnPath
        if (!previousUrl) {
          previousUrl = sessionStorage.getItem("previousURL");

          if (previousUrl) {
            sessionStorage.removeItem("previousURL");
          }
        }

        // Parse isTablet from multiple sources in the URL
        // 1. Query parameter: ?isTablet=true
        const isTabletFromQuery = search.isTablet === "true";

        // 2. Check if previousUrl contains /isTablet=true or /isTablet (path segment)
        const isTabletFromPreviousUrl =
          previousUrl &&
          (previousUrl.includes("/isTablet=true") ||
            previousUrl.includes("/isTablet"));

        // 3. Check localStorage for isTablet flag
        const isTabletFromLocalStorage =
          localStorage.getItem("isTablet") === "true";

        // Combined check - isTablet is true if found in ANY location
        const isTabletParam =
          isTabletFromQuery ||
          isTabletFromPreviousUrl ||
          isTabletFromLocalStorage;

        // Persist isTablet to localStorage if detected from URL or previousUrl
        if (isTabletParam && !isTabletFromLocalStorage) {
          localStorage.setItem("isTablet", "true");
        }

        // Determine the user's role-based route
        const userRoleRoute = roles.some((role) =>
          ACCESS_LEVELS[ADMINS].includes(role)
        )
          ? "admin"
          : roles.some((role) => ACCESS_LEVELS[STUDENT].includes(role))
            ? "student"
            : "teacher";

        // Determine redirect target based on previousUrl or default to role-based home
        // HealthCheckGuard will handle showing toast for restricted routes in tablet mode
        let redirectTarget;
        if (!previousUrl) {
          redirectTarget = `/${userRoleRoute}`;
        } else {
          // Transform the path to match the logged-in user's role
          const pathSegments = previousUrl.split("/").filter(Boolean);
          if (pathSegments.length > 0) {
            const firstSegment = pathSegments[0];
            if (
              firstSegment === "admin" ||
              firstSegment === "teacher" ||
              firstSegment === "student"
            ) {
              pathSegments[0] = userRoleRoute;
              redirectTarget = "/" + pathSegments.join("/");
            } else {
              redirectTarget = previousUrl;
            }
          } else {
            redirectTarget = previousUrl;
          }
        }

        // Check if we're in an iframe
        const isInIframe = window.self !== window.top;

        const diagnosticData = {
          isTabletFromQuery,
          isTabletFromPreviousUrl,
          isTabletFromLocalStorage,
          isTabletParam,
          roles,
          previousUrl,
          userRoleRoute,
          redirectTarget,
          isInIframe
        };

        // CRITICAL: If we're in an iframe, send diagnostic data to parent window
        // and let parent handle the navigation
        if (isInIframe) {
          window.top.postMessage(
            {
              type: "MYBESTUSER_LOGIN_DIAGNOSTIC",
              data: diagnosticData
            },
            "*"
          );
        } else {
          // Not in iframe, navigate directly
          navigate(redirectTarget);
        }
      })
      .catch((error) => {
        dispatch({
          type: SET_WARNING,
          payload: {
            alert: error.message,
            confirm: () => {
              dispatch({ type: CLOSE_WARNING, payload: {} });
              navigate("/");
            }
          }
        });
      });
  }, [signinWithCode, navigate, location, dispatch]);

  return null;
};

export default MyBestUserLogin;
